import React from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { IconButton, LinkButton } from '@atlaskit/button/new';
import DepartmentIcon from '@atlaskit/icon/core/department';
import { Inline } from '@atlaskit/primitives/compiled';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';

import { type PersonProfileData } from '../../types';

const messages = defineMessages({
	viewProfile: {
		id: 'user-profile-preview.action-bar.view-profile',
		defaultMessage: 'View profile',
		description: 'Button label to navigate to a user profile',
	},
	sendMessageLabel: {
		id: 'user-profile-preview.action-bar.send-message-label',
		defaultMessage: 'Send message to {name}',
		description: 'Accessible label for the send message button',
	},
	viewOrgChartLabel: {
		id: 'user-profile-preview.action-bar.view-org-chart-label',
		defaultMessage: 'View org chart for {name}',
		description: 'Accessible label for the org chart icon button',
	},
});

interface ActionBarProps {
	data: PersonProfileData;
	showOrgChart?: boolean;
	onViewOrgChart?: (accountId: string) => void;
	cloudId: string;
}

export function ActionBar({ data, showOrgChart = true, onViewOrgChart, cloudId }: ActionBarProps) {
	const intl = useIntl();
	const { accountId, name } = data;
	const { href, target, onNavigate } = navigateToTeamsApp({
		type: 'USER',
		payload: { userId: accountId },
		cloudId: cloudId ?? '',
	});

	return (
		<Inline space="space.100" alignBlock="center">
			{(showOrgChart || onViewOrgChart) && (
				<IconButton
					icon={DepartmentIcon}
					label={intl.formatMessage(messages.viewOrgChartLabel, { name })}
					onClick={() => onViewOrgChart?.(accountId)}
				/>
			)}
			{accountId && href && (
				<LinkButton
					href={href}
					appearance="default"
					target={target}
					onClick={onNavigate}
					rel={target === '_blank' ? 'noopener noreferrer' : undefined}
				>
					{intl.formatMessage(messages.viewProfile)}
				</LinkButton>
			)}
		</Inline>
	);
}
